import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Navigation.css';

const navLinks = [
  { name: 'Home', id: 'home' },
  { name: 'Services', id: 'services' },
  { name: 'Pricing', id: 'pricing' },
  { name: 'FAQ', id: 'faq' },
  { name: 'About', id: 'about' },
  { name: 'Contact', id: 'contact' },
];

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [activeSection, setActiveSection] = useState('home');
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isHomePage) {
      setActiveSection('');
      return undefined;
    }

    const handleScroll = () => {
      const sections = navLinks.map(link => link.id);
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const { offsetTop, offsetHeight } = section;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const scrollToSection = (sectionId) => {
    if (!isHomePage) {
      navigate(`/#${sectionId}`);
      setMobileMenuOpen(false);
      return;
    }

    const section = document.getElementById(sectionId);
    if (section) {
      const offsetTop = section.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  const goHome = () => {
    if (isHomePage) {
      scrollToSection('home');
      return;
    }

    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navigation" role="navigation" aria-label="Main navigation">
      <div className="nav-container">
        <button 
          onClick={goHome} 
          className="nav-logo"
          aria-label="Leviathan Coding Systems - Home"
        >
          <span className="logo-icon" aria-hidden="true">&gt;_</span>
          <span className="logo-text">Leviathan</span>
        </button>

        <div className="nav-links">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className={`nav-link ${activeSection === link.id ? 'active' : ''}`}
              onMouseEnter={() => setHoveredLink(link.id)}
              onMouseLeave={() => setHoveredLink(null)}
              aria-label={`Navigate to ${link.name} section`}
              aria-current={activeSection === link.id ? 'page' : undefined}
            >
              {link.name}
              {activeSection === link.id ? (
                <motion.div
                  className="underline"
                  layoutId="underline"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              ) : hoveredLink === link.id ? (
                <motion.div
                  className="underline-hover"
                  initial={{ scaleX: 0, originX: 0 }}
                  animate={{ scaleX: 1 }}
                  exit={{ scaleX: 0 }}
                  transition={{ duration: 0.3 }}
                />
              ) : null}
            </button>
          ))}
        </div>

        <button onClick={() => scrollToSection('contact')} className="btn-get-started">
          Get Started
        </button>

        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <motion.div
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
        initial={false}
        animate={mobileMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: '100%' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className="mobile-menu-content">
          {navLinks.map((link) => (
            <motion.button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className={`mobile-nav-link ${activeSection === link.id ? 'active' : ''}`}
              initial={{ opacity: 0, x: 50 }}
              animate={mobileMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ delay: navLinks.indexOf(link) * 0.1 }}
            >
              {link.name}
              {activeSection === link.id && (
                <span className="mobile-active-indicator">•</span>
              )}
            </motion.button>
          ))}
          <motion.button
            onClick={() => scrollToSection('contact')}
            className="mobile-cta-button"
            initial={{ opacity: 0, y: 20 }}
            animate={mobileMenuOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.4 }}
          >
            Get Started
          </motion.button>
        </div>
      </motion.div>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <motion.div
          className="mobile-menu-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navigation;
